import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  getAgreementSignatureSavedValues,
  isAgreementSignatureDateFieldValid,
  isAgreementSignatureFieldSchemaValid,
  isAgreementSignatureFieldUnchanged,
  persistAgreementSignatureFieldSave,
} from './utils';

import type { FormikProps } from 'formik';
import type { AppDispatch } from '../../../../../../../store';
import type {
  AgreementSignatureFieldName,
  AgreementSignatureSavedFieldValues,
  SaveAgreementPrintedNameOnBlur,
  SaveAgreementSignature,
  SaveAgreementSignatureDateOnChange,
} from './types';
import type { AgreementFormValues } from '../../../../types';

export default function useSaveAgreementSignatureFields(
  formik: FormikProps<AgreementFormValues>,
  initialValues: AgreementFormValues,
) {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { onboardingQuestionnaireId } = useParams<'onboardingQuestionnaireId'>();
  const savedFieldValuesRef = useRef<AgreementSignatureSavedFieldValues>(
    getAgreementSignatureSavedValues(initialValues),
  );
  const savingPromiseRef = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    savedFieldValuesRef.current = getAgreementSignatureSavedValues(initialValues);
  }, [initialValues]);

  const saveField = useCallback(
    async (name: AgreementSignatureFieldName, value?: AgreementFormValues[typeof name]) => {
      if (!onboardingQuestionnaireId) {
        return;
      }

      const values = value === undefined ? formik.values : { ...formik.values, [name]: value };

      if (!isAgreementSignatureDateFieldValid(values)) {
        return;
      }

      if (!(await isAgreementSignatureFieldSchemaValid(name, values))) {
        return;
      }

      if (isAgreementSignatureFieldUnchanged(name, values, savedFieldValuesRef.current)) {
        return;
      }

      const previousSave = savingPromiseRef.current;
      const currentSave = (async () => {
        const saved = await persistAgreementSignatureFieldSave({
          previousSave,
          dispatch,
          onboardingQuestionnaireId,
          values,
          getAccessTokenSilently,
          formik,
        });

        if (saved) {
          savedFieldValuesRef.current = saved;
        }
      })();

      savingPromiseRef.current = currentSave;
      await currentSave;
    },
    [dispatch, formik, getAccessTokenSilently, onboardingQuestionnaireId],
  );

  const savePrintedNameOnBlur = useCallback<SaveAgreementPrintedNameOnBlur>(
    async (name) => {
      if (name !== 'printed_name') {
        return;
      }

      await saveField('printed_name');
    },
    [saveField],
  );

  const saveSignatureDateOnChange = useCallback<SaveAgreementSignatureDateOnChange>(
    async (name, value) => {
      if (name !== 'signature_date') {
        return;
      }

      await saveField('signature_date', value);
    },
    [saveField],
  );

  const saveSignature = useCallback<SaveAgreementSignature>(
    async (signature) => {
      await formik.setFieldValue('signature', signature, false);
      await saveField('signature', signature);
    },
    [formik, saveField],
  );

  return {
    savePrintedNameOnBlur,
    saveSignatureDateOnChange,
    saveSignature,
  };
}
